import type {
  ApiListParams,
  BookingListParams,
  BookingShipmentActivityFilter,
  BookingShipmentFilter,
} from '@dc-trans/data-layer';
import {
  getBookingActivities,
  getBookingById,
  getBookingCount,
  getBookingData,
  getBookingShipment,
  getBookingShipmentActivities,
  getBookingShipmentGroupedV2,
} from '@dc-trans/data-layer';
import { isAxiosError } from 'axios';

import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

export const bookingKeys = {
  all: ['booking'] as const,
  list: (params?: BookingListParams) => [...bookingKeys.all, 'list', params] as const,
  count: () => [...bookingKeys.all, 'count'] as const,
};

export const useBookingList = (params?: BookingListParams) => {
  return useQuery({
    queryKey: bookingKeys.list(params),
    queryFn: () => getBookingData(params),
  });
};

export const useBookingCount = () => {
  return useQuery({
    queryKey: bookingKeys.count(),
    queryFn: () => getBookingCount(),
  });
};

export const useBookingShipment = (params: ApiListParams<BookingShipmentFilter>) => {
  return useQuery({
    queryKey: [...bookingKeys.all, 'shipment', params],
    queryFn: () => getBookingShipment(params),
  });
};

export const useBookingShipmentInfinite = (params: ApiListParams<BookingShipmentFilter>, enabled?: boolean) => {
  return useInfiniteQuery({
    queryKey: [...bookingKeys.all, params.filters?.booking_ulid, 'shipment', params],
    queryFn: ({ pageParam = 1 }) => getBookingShipment({ ...params, page: pageParam as number }),
    getNextPageParam: lastPage => {
      if (lastPage.data.items.length === lastPage.data.meta.limit) {
        return lastPage.data.meta.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled,
  });
};

export const useBookingById = (id: string) => {
  return useQuery({
    queryKey: [...bookingKeys.all, id],
    queryFn: () => getBookingById(id),
    enabled: !!id,
    meta: {
      skip: (context: any) => isAxiosError(context.error) && context.error.response?.status === 403,
    },
  });
};

export const useBookingShipmentActivity = (id: string, params: ApiListParams<BookingShipmentActivityFilter>) => {
  return useQuery({
    queryKey: [...bookingKeys.all, id, 'shipment-activity', params],
    queryFn: () => getBookingShipmentActivities(id, params),
    enabled: !!id,
  });
};

export const useBookingShipmentGroupedV2 = (id: string, params: ApiListParams<any>) => {
  return useQuery({
    queryKey: [...bookingKeys.all, id, 'shipment-grouped-v2', params],
    queryFn: () => getBookingShipmentGroupedV2(id, params),
    enabled: !!id,
  });
};

export const useBookingActivities = (id: string, params: ApiListParams<any>) => {
  return useQuery({
    queryKey: [...bookingKeys.all, id, 'activities', params],
    queryFn: () => getBookingActivities(id, params),
    enabled: !!id,
  });
};
